<template>
  <div class="dialogs-list">
    <article
      v-for="dialog in dialogs"
      :key="dialog.id"
      :class="['dialogs-list-item', { 'is-active': isSelected(dialog) }]"
      @click="$emit('select-dialog', dialog)"
    >
      <div class="dialogs-list-item__main">
        <el-avatar
          :size="54"
          :src="getAvatar(dialog)"
          class="dialogs-list-item__avatar"
        >
          <template v-if="!getAvatar(dialog)">{{ clientName(dialog.client_name) }}</template>
        </el-avatar>

        <div class="dialogs-list-item__body">
          <div class="dialogs-list-item__head">
            <div class="dialogs-list-item__title">
              <span class="dialogs-list-item__name">{{ dialog.client_name || 'Без имени' }}</span>
              <span v-if="dialog.messenger_type" class="dialogs-list-item__messenger">
                <img
                  v-if="getMessengerAsset(dialog.messenger_type)"
                  :src="getMessengerAsset(dialog.messenger_type)"
                  alt=""
                  class="dialogs-list-item__messenger-image"
                />
                <font-awesome-icon
                  v-else
                  :class="`dialogs-list-item__messenger--${dialog.messenger_type}`"
                  :icon="getMessengerIcon(dialog.messenger_type)"
                />
              </span>
            </div>
            <span class="dialogs-list-item__time">{{ formatLastMessageDate(dialog.last_active) }}</span>
          </div>

          <div class="dialogs-list-item__footer">
            <p class="dialogs-list-item__preview">{{ previewText(dialog) }}</p>
            <span v-if="Number(dialog.unread_count || 0) > 0" class="dialogs-list-item__badge">
              {{ dialog.unread_count }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="isSelected(dialog)" class="dialogs-list-item__actions" aria-hidden="true">
        <span class="dialogs-list-action dialogs-list-action--archive">
          <img src="/img/dialogs/archive-icon.png" alt="" class="dialogs-list-action__icon" />
          <small>Архив</small>
        </span>
        <span class="dialogs-list-action dialogs-list-action--delete">
          <img src="/img/dialogs/delete-icon.png" alt="" class="dialogs-list-action__icon" />
          <small>Удалить</small>
        </span>
      </div>
    </article>

    <div v-if="!dialogs.length" class="dialogs-list-empty">Диалоги не найдены</div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'DialogsList',
  components: {
    FontAwesomeIcon
  },
  props: {
    dialogs: {
      type: Array,
      default: () => []
    },
    selectedDialogId: {
      type: [Number, String],
      default: null
    }
  },
  methods: {
    isSelected(dialog) {
      return String(this.selectedDialogId) === String(dialog.id)
    },
    clientName(name) {
      if (!name) return ''
      const parts = name.trim().split(' ').filter(Boolean)
      if (parts.length === 0) return ''
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase()
      }
      return (parts[0][0] + parts[1][0]).toUpperCase()
    },
    getMessengerIcon(type) {
      switch (type) {
        case 'whatsapp':
          return ['fab', 'whatsapp']
        case 'telegram':
          return ['fab', 'telegram']
        case 'vk':
          return ['fab', 'vk']
        case 'email':
          return ['far', 'envelope']
        default:
          return ['far', 'circle-question']
      }
    },
    getMessengerAsset(type) {
      switch (type) {
        case 'whatsapp':
          return '/img/dialogs/messenger-whatsapp.png'
        case 'telegram':
          return '/img/dialogs/messenger-telegram.png'
        case 'vk':
          return '/img/dialogs/messenger-vk.png'
        case 'facebook':
          return '/img/dialogs/messenger-facebook.png'
        case 'email':
          return '/img/dialogs/messenger-email.png'
        default:
          return ''
      }
    },
    getAvatar(dialog) {
      if (dialog.client_avatar) return dialog.client_avatar
      if (dialog.client_sex === 1) return '/img/avatar-male.svg'
      if (dialog.client_sex === 2) return '/img/avatar-female.svg'
      return null
    },
    previewText(dialog) {
      return dialog.last_message_preview || dialog.last_message || dialog.last_message_text || dialog.client_phone || 'Диалог без сообщений'
    },
    formatLastMessageDate(date) {
      if (!date) return ''
      const parsed = new Date(String(date).replace(' ', 'T'))
      if (Number.isNaN(parsed.getTime())) return ''

      const now = new Date()
      const today = parsed.toDateString() === now.toDateString()
      const yesterday = new Date(now)
      yesterday.setDate(now.getDate() - 1)
      const isYesterday = parsed.toDateString() === yesterday.toDateString()

      if (today) {
        return parsed.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
      }
      if (isYesterday) {
        return 'Вчера'
      }

      const diffDays = Math.floor((now - parsed) / 86400000)
      if (diffDays < 7) {
        const weekday = parsed.toLocaleDateString('ru-RU', { weekday: 'short' })
        return weekday.slice(0, 1).toUpperCase() + weekday.slice(1, 2)
      }

      return parsed.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
    }
  }
}
</script>

<style scoped>
.dialogs-list {
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 185, 255, 0.26) transparent;
}

.dialogs-list::-webkit-scrollbar {
  width: 10px;
}

.dialogs-list::-webkit-scrollbar-track {
  background: transparent;
}

.dialogs-list::-webkit-scrollbar-thumb {
  border: 3px solid transparent;
  border-radius: 999px;
  background: rgba(148, 185, 255, 0.22);
  background-clip: padding-box;
}

.dialogs-list-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  overflow: hidden;
  border-radius: 12px;
  background: linear-gradient(180deg, #173f7d 0%, #10274f 100%);
  box-shadow: inset 0 0 0 1px rgba(48, 106, 196, 0.58);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.dialogs-list-item:hover {
  transform: translateY(-1px);
  box-shadow: inset 0 0 0 1px rgba(87, 145, 236, 0.82), 0 10px 24px rgba(4, 14, 36, 0.2);
}

.dialogs-list-item.is-active {
  grid-template-columns: minmax(0, 1fr) 136px;
  background: linear-gradient(180deg, #143365 0%, #0f2246 100%);
}

.dialogs-list-item__main {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  min-height: 72px;
  min-width: 0;
  padding: 9px 14px 9px 12px;
}

.dialogs-list-item__avatar {
  width: 54px;
  height: 54px;
  flex-shrink: 0;
  background: #e8edf4;
  color: #45607f;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 18px;
  box-shadow: 0 6px 14px rgba(5, 18, 44, 0.2);
}

.dialogs-list-item__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dialogs-list-item__head,
.dialogs-list-item__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.dialogs-list-item__title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 7px;
}

.dialogs-list-item__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ffffff;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.05;
}

.dialogs-list-item__messenger {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.dialogs-list-item__messenger-image {
  display: block;
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.dialogs-list-item__messenger--telegram,
.dialogs-list-item__messenger--whatsapp,
.dialogs-list-item__messenger--vk,
.dialogs-list-item__messenger--email,
.dialogs-list-item__messenger--facebook {
  font-size: 14px;
}

.dialogs-list-item__time {
  flex-shrink: 0;
  color: #ffffff;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 15px;
  line-height: 1;
}

.dialogs-list-item__preview {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #00c3fe;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 13px;
  line-height: 1.1;
}

.dialogs-list-item__badge {
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #0db4ff;
  color: #07162f;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.dialogs-list-item.is-active .dialogs-list-item__preview {
  color: #d7e6ff;
}

.dialogs-list-item__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-width: 136px;
  border-left: 1px solid rgba(83, 136, 221, 0.72);
}

.dialogs-list-action {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: #ffffff;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  text-align: center;
}

.dialogs-list-action__icon {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.dialogs-list-action--archive {
  background: linear-gradient(180deg, #4477c8 0%, #2f5ea9 100%);
}

.dialogs-list-action--delete {
  background: linear-gradient(180deg, #d6201f 0%, #b90f0f 100%);
}

.dialogs-list-action small {
  font-size: 12px;
  font-weight: 300;
}

.dialogs-list-empty {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(180deg, #133264 0%, #0f2751 100%);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.5);
  color: #b9d5ff;
  font-family: "Oswald", sans-serif;
  font-size: 18px;
}

@media (max-width: 900px) {
  .dialogs-list-item__main {
    min-height: 80px;
  }
}

@media (max-width: 540px) {
  .dialogs-list-item.is-active {
    grid-template-columns: minmax(0, 1fr) 116px;
  }

  .dialogs-list-item__main {
    padding: 12px;
  }

  .dialogs-list-item__name {
    font-size: 17px;
  }

  .dialogs-list-item__preview,
  .dialogs-list-item__time {
    font-size: 14px;
  }

  .dialogs-list-item__actions {
    min-width: 116px;
  }

  .dialogs-list-action__icon {
    width: 22px;
    height: 22px;
  }

  .dialogs-list-action small {
    font-size: 11px;
  }
}
</style>
